'use client';

import { useEffect, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { Plus } from 'lucide-react';
import { AuthTokenService } from '@/shared/lib/auth-token-service';
import { useProjects } from '@/features/project/hooks/use-projects';
import { useLastProject } from '@/shared/hooks/use-last-project';
import { NewProjectDialog, useNewProjectStore } from '@/features/project';
import { Button } from '@/shared/ui/shadcn/button';

interface PageProps {
  params: Promise<Record<string, never>>;
  searchParams: Promise<Record<string, never>>;
}

/**
 * Root client resolver — the building page is the app home now (the Dashboard
 * was removed). For an authenticated user we resolve the active building
 * (last-opened → first) and redirect to `/project/{id}`. With no buildings we
 * show a first-run "create your first building" state. Unauthenticated users go
 * to login. Auth-token presence is read synchronously so there is no flash of
 * the wrong destination before the router fires.
 */
export default function Home(_props: PageProps) {
  const t = useTranslations('common');
  const tGrid = useTranslations('projectGrid');
  const router = useRouter();
  const openNewProject = useNewProjectStore((s) => s.open);

  const hasToken =
    typeof window !== 'undefined' ? !!AuthTokenService.getTokenSync() : false;

  const { data, isLoading } = useProjects({ enabled: hasToken });
  const projects = data?.data;
  const { lastProjectId } = useLastProject();

  const targetProjectId = useMemo(() => {
    if (!projects || projects.length === 0) return null;
    if (lastProjectId && projects.some((p) => p.id === lastProjectId)) {
      return lastProjectId;
    }
    return projects[0]?.id ?? null;
  }, [projects, lastProjectId]);

  useEffect(() => {
    if (!hasToken) {
      router.replace('/auth/login');
      return;
    }
    if (targetProjectId) {
      router.replace(`/project/${targetProjectId}`);
    }
  }, [hasToken, targetProjectId, router]);

  // Authenticated but no buildings yet → first-run create state.
  if (hasToken && !isLoading && projects && projects.length === 0) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center">
        <Button onClick={openNewProject}>
          <Plus className="h-4 w-4" />
          {tGrid('createFirst')}
        </Button>
        <NewProjectDialog />
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-foreground mx-auto"></div>
        <p className="mt-4 text-muted-foreground">{t('redirecting')}</p>
      </div>
    </div>
  );
}
